import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import { API_URL } from '../config';
import {
  Syringe,
  Sparkles,
  AlertTriangle,
  Info,
  Shield,
  CheckCircle2,
  XCircle,
  Droplets,
  Brain,
  Activity,
  Clock,
  Pill
} from 'lucide-react';
import { toast } from 'sonner';

export default function PonctionLombaireePage() {
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAiAnalysis = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Veuillez décrire la situation clinique');
      return;
    }

    setLoading(true);
    try {
      const context = `Ponction Lombaire - Aide à la décision:\n\n${aiPrompt}`;

      const response = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: context,
          context: 'ponction_lombaire_aide'
        })
      });

      if (!response.ok) throw new Error('Erreur lors de l\'analyse IA');

      const data = await response.json();
      setAiResponse(data.response);
      toast.success('Analyse IA terminée');
    } catch (error) {
      console.error('AI Analysis Error:', error);
      toast.error('Erreur lors de l\'analyse IA');
      setAiResponse('Erreur lors de l\'analyse. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ponction-lombaire-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold glow-text flex items-center gap-3">
            <Syringe className="w-8 h-8 text-cyan-400" />
            Ponction Lombaire
          </h1>
          <p className="text-[hsl(var(--text-dim))] mt-2">
            Guide technique complet avec analyse IA
          </p>
        </div>
      </div>

      {/* Alert Info */}
      <Alert className="bg-cyan-500/10 border-cyan-500/30">
        <Info className="w-5 h-5 text-cyan-400" />
        <AlertDescription className="text-cyan-300">
          <strong>Prérequis :</strong> Vérifier les contre-indications avant toute réalisation de ponction lombaire
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Protocole Technique */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-cyan-400" />
                Protocole de Réalisation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="space-y-2">
                {/* A. Avant le geste */}
                <AccordionItem value="avant" className="border border-cyan-500/50 rounded-lg px-4 bg-cyan-500/5">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-cyan-500/20">
                        <CheckCircle2 className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-cyan-400">A. Avant la Réalisation du Geste</div>
                        <div className="text-xs text-cyan-300">Matériel, CI, Prémédication</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 mt-3">
                      {/* Matériel */}
                      <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                        <p className="text-sm font-semibold text-cyan-300 mb-2 flex items-center gap-2">
                          <Activity className="w-4 h-4" />
                          1. Matériel nécessaire
                        </p>
                        <ul className="space-y-1 text-xs text-white">
                          <li>• Trocart à ponction lombaire (PL)</li>
                          <li>• Savon et solution antiseptique (chlorhexidine alcoolique)</li>
                          <li>• Gants stériles, masque et charlotte</li>
                          <li>• Tubes stériles : <strong>5 au moins</strong> en cas de suspicion de méningite</li>
                          <li>• Compresses stériles</li>
                          <li>• Champs stériles</li>
                        </ul>
                      </div>

                      {/* Contre-indications */}
                      <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                        <p className="text-sm font-semibold text-red-300 mb-2 flex items-center gap-2">
                          <XCircle className="w-4 h-4" />
                          2. Contre-indications
                        </p>
                        <ul className="space-y-1 text-xs text-white">
                          <li className="flex items-start gap-2">
                            <span className="text-red-400 mt-1">•</span>
                            <span>Troubles neurologiques focaux ou suspicion clinique d&apos;HTIC → <strong>réalisation d&apos;une TDM</strong></span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-400 mt-1">•</span>
                            <span>Thrombopénie marquée (plaquettes {"<"} 50 000/mm³)</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-400 mt-1">•</span>
                            <span>Trouble de la coagulation ou traitement anticoagulant à dose efficace</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-400 mt-1">•</span>
                            <span>Infection cutanée en regard du point de ponction</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-red-400 mt-1">•</span>
                            <span>Instabilité hémodynamique ou respiratoire</span>
                          </li>
                        </ul>
                      </div>

                      {/* Prémédication */}
                      <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                        <p className="text-sm font-semibold text-purple-300 mb-2 flex items-center gap-2">
                          <Pill className="w-4 h-4" />
                          3. Prémédication (en l&apos;absence d&apos;urgence)
                        </p>
                        <ul className="space-y-1 text-xs text-white">
                          <li>• Patch d&apos;Emla® : <strong>1 heure avant le geste</strong></li>
                          <li>• Atarax® : 50 à 100 mg IVL en l&apos;absence de troubles de conscience et en cas d&apos;anxiété</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* B. Réalisation du geste */}
                <AccordionItem value="realisation" className="border border-blue-500/50 rounded-lg px-4 bg-blue-500/5">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-500/20">
                        <Syringe className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-blue-400">B. Réalisation du Geste</div>
                        <div className="text-xs text-blue-300">Position, Asepsie, Technique</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 mt-3">
                      {/* Position */}
                      <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                        <p className="text-sm font-semibold text-blue-300 mb-2">1. Position du patient</p>
                        <ul className="space-y-1 text-xs text-white">
                          <li>• <strong>Position assise, dos fortement courbé en avant</strong></li>
                          <li>• Décubitus latéral en cas de trouble de conscience ou difficulté à maintenir la position</li>
                          <li>• Utilisation de MEOPA® peut être proposée</li>
                          <li className="mt-2 pt-2 border-t border-blue-500/30">
                            <strong>Repérage :</strong>
                            <ul className="ml-4 mt-1 space-y-1">
                              <li>→ Espace interépineux L3-L4, L4-L5 et L5-S1</li>
                              <li>→ En dessous d&apos;une ligne horizontale entre les deux crêtes iliaques</li>
                            </ul>
                          </li>
                        </ul>
                      </div>

                      {/* Asepsie */}
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                        <p className="text-sm font-semibold text-green-300 mb-2 flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          2. Asepsie rigoureuse
                        </p>
                        <div className="space-y-2 text-xs text-white">
                          <div>
                            <strong>Préparation cutanée :</strong>
                            <ul className="ml-4 mt-1">
                              <li>• Désinfection cutanée en 2 temps</li>
                              <li>• Champ stérile</li>
                            </ul>
                          </div>
                          <div>
                            <strong>Opérateur :</strong>
                            <ul className="ml-4 mt-1">
                              <li>• Masque</li>
                              <li>• Désinfection des mains</li>
                              <li>• Gants stériles</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Technique du geste */}
                      <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                        <p className="text-sm font-semibold text-yellow-300 mb-2">3. Technique du geste</p>
                        <div className="space-y-2 text-xs text-white">
                          <Alert className="bg-cyan-500/10 border-cyan-500/30">
                            <Info className="w-4 h-4 text-cyan-400" />
                            <AlertDescription className="text-cyan-300 text-xs">
                              <strong>Aiguille recommandée :</strong> Aiguille atraumatique à extrémité non tranchante, type « Sprotte » ou « Whitacre », avec introducteur pour prévenir le syndrome post-PL (aiguille noire ≥ 22 G)
                            </AlertDescription>
                          </Alert>
                          
                          <ul className="space-y-2 mt-3">
                            <li className="flex items-start gap-2">
                              <span className="text-yellow-400 mt-1">1.</span>
                              <span>Enfoncer le trocart <strong>biseau sur le côté</strong> (parallèle aux fibres de la dure-mère)</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-yellow-400 mt-1">2.</span>
                              <div>
                                <strong>Prélever au moins 5 tubes lors des méningites</strong> (10 gouttes par tube) :
                                <ul className="ml-4 mt-1 space-y-1">
                                  <li>→ 1 tube de biochimie</li>
                                  <li>→ 1 tube de cytobactériologie</li>
                                  <li>→ 1 tube pour PCR virales</li>
                                  <li>→ 1 tube PCR Listeria</li>
                                  <li>→ 1 tube PCR BK</li>
                                </ul>
                              </div>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-yellow-400 mt-1">3.</span>
                              <span><strong>Réserver systématiquement le dernier tube prélevé</strong> pour la cytobactériologie</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-yellow-400 mt-1">4.</span>
                              <span>Réintroduction du mandrin avant retrait</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-yellow-400 mt-1">5.</span>
                              <span><strong>Glycémie capillaire systématique</strong> au moment de la ponction</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* C. Après le geste */}
                <AccordionItem value="apres" className="border border-green-500/50 rounded-lg px-4 bg-green-500/5">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-500/20">
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-green-400">C. Après le Geste</div>
                        <div className="text-xs text-green-300">Surveillance et complications</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 mt-3">
                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                        <ul className="space-y-2 text-xs text-white">
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                            <span><strong>Mobilisation précoce du patient possible</strong></span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                            <span>Pas d&apos;hydratation systématique</span>
                          </li>
                        </ul>
                      </div>

                      <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                        <p className="text-sm font-semibold text-orange-300 mb-2">Complications possibles :</p>
                        <ul className="space-y-1 text-xs text-white">
                          <li>• Hématome au point de ponction par blessure vasculaire</li>
                          <li>• Paresthésies des membres inférieurs à type de décharge électrique</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* D. Céphalées post-PL */}
                <AccordionItem value="cephalees" className="border border-purple-500/50 rounded-lg px-4 bg-purple-500/5">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-purple-500/20">
                        <Brain className="w-5 h-5 text-purple-400" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-purple-400">D. Céphalées Post-PL</div>
                        <div className="text-xs text-purple-300">Définition et traitement</div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 mt-3">
                      {/* Définition */}
                      <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                        <p className="text-sm font-semibold text-purple-300 mb-2">1. Définition</p>
                        <p className="text-xs text-white leading-relaxed">
                          Céphalée post-ponction lombaire, <strong>posturale</strong> (augmente à l&apos;orthostatisme), 
                          frontale ou occipitale, parfois unilatérale, avec signes vestibulocochléaires fréquents 
                          d&apos;intensité variable. <strong>Disparaît spontanément en 5 à 10 jours.</strong> 
                          Elle est due à une fuite persistante de LCS.
                        </p>
                      </div>

                      {/* Traitement */}
                      <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                        <p className="text-sm font-semibold text-purple-300 mb-2">2. Traitement</p>
                        <div className="space-y-3 text-xs text-white">
                          <div>
                            <p className="font-semibold text-cyan-300 mb-1">Traitement médicamenteux (1ère intention) :</p>
                            <ul className="ml-4 space-y-1">
                              <li>• Paracétamol 1 g IVL</li>
                              <li>• Hydratation PO</li>
                              <li>• Repos en décubitus</li>
                            </ul>
                          </div>

                          <div className="pt-2 border-t border-purple-500/30">
                            <p className="font-semibold text-yellow-300 mb-1">Si échec (2ème intention) :</p>
                            <ul className="ml-4 space-y-1">
                              <li>• <strong>Caféine</strong> : 200 à 500 mg/j = 4 tasses de café PO</li>
                              <li>• OU <strong>Claradol caféiné®</strong> (caféine + paracétamol) : 2 cp × 3/jour</li>
                            </ul>
                          </div>

                          <div className="pt-2 border-t border-purple-500/30">
                            <p className="font-semibold text-orange-300 mb-1">Blood-patch (3ème intention) :</p>
                            <ul className="ml-4 space-y-1">
                              <li>• <strong>Injection de sang (20 mL)</strong> du patient dans l&apos;espace péridural pour colmater la brèche</li>
                              <li>• Après échec du traitement médicamenteux</li>
                              <li>• Peut être renouvelé une fois</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </div>

        {/* Panel IA */}
        <div className="space-y-4">
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                Analyse IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Décrivez la situation clinique...&#10;&#10;Exemple: Patient 65 ans, suspicion de méningite, plaquettes 35000/mm³. Peut-on réaliser une PL ? Quelles précautions ?"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={8}
                className="resize-none"
              />

              <Button
                onClick={handleAiAnalysis}
                disabled={loading || !aiPrompt.trim()}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyser avec IA
                  </>
                )}
              </Button>

              {aiResponse && (
                <div className="p-4 bg-[hsl(var(--card-hover))] rounded-lg border border-cyan-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-5 h-5 text-cyan-400" />
                    <span className="font-semibold text-white">Recommandation IA</span>
                  </div>
                  <div className="text-sm text-white whitespace-pre-wrap leading-relaxed">
                    {aiResponse}
                  </div>
                  <Alert className="mt-3 bg-yellow-500/10 border-yellow-500/30">
                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                    <AlertDescription className="text-yellow-300 text-xs">
                      Cette analyse IA est une aide à la décision. Elle ne remplace pas le jugement clinique et l&apos;avis spécialisé.
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Reference Card */}
          <Card className="border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-cyan-400" />
                Points Clés
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-xs">
                <div className="p-2 bg-red-500/10 rounded border border-red-500/30">
                  <p className="font-semibold text-red-300 mb-1">⚠️ CI absolues</p>
                  <p className="text-red-200">HTIC, Plaquettes {"<"}50k, Infection cutanée</p>
                </div>
                <div className="p-2 bg-blue-500/10 rounded border border-blue-500/30">
                  <p className="font-semibold text-blue-300 mb-1">🎯 Repérage</p>
                  <p className="text-blue-200">L3-L4, L4-L5, L5-S1 sous crêtes iliaques</p>
                </div>
                <div className="p-2 bg-green-500/10 rounded border border-green-500/30">
                  <p className="font-semibold text-green-300 mb-1">💉 Aiguille</p>
                  <p className="text-green-200">Sprotte/Whitacre ≥22G, biseau latéral</p>
                </div>
                <div className="p-2 bg-purple-500/10 rounded border border-purple-500/30">
                  <p className="font-semibold text-purple-300 mb-1">🧪 Tubes</p>
                  <p className="text-purple-200">5 tubes minimum pour méningite</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
